import { Modal } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormTextInput, FormSubmitButton, GlobalButton, Authorize } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { typeSchema, TypeSchema, defaultTypeValues } from '../models';
import { useTypesActions, useTypes } from '../hooks';
import { ROLES } from '@/components/security/AppRoles';

interface AddTypeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (type: { id: string; name: string }) => void;
}

const AddTypeModal = ({ open, onClose, onSuccess }: AddTypeModalProps) => {
  const { handleSubmit, control, reset, watch } = useForm<TypeSchema>({
    resolver: zodResolver(typeSchema),
    defaultValues: defaultTypeValues,
  });

  const { onCreateType, isCreatingType } = useTypesActions();
  const { types } = useTypes({ pageSize: 1000 });
  
  const typeName = watch('name');
  const existingType = types.find(
    type => type.name.toLowerCase() === typeName?.toLowerCase()
  );

  const onSubmit = async (data: TypeSchema) => {
    if (existingType) {
      onSuccess({ id: existingType.id, name: existingType.name });
      onClose();
      return;
    }
    
    try {
      const result = await onCreateType(data, reset);
      onSuccess({ id: result.id, name: result.name });
      onClose();
    } catch (error) {
      console.error('Error creating type:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Authorize permission={ROLES.INVENTORY.admin}>
      <Modal open={open} onClose={handleClose}>
        <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
          <div className='flex flex-col p-10 gap-4 w-[500px] bg-white outline-none rounded-sm shadow-xl'>
            <div className='flex justify-between items-center'>
              <h2 className='text-xl font-bold'>Agregar nuevo tipo</h2>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
              <FormTextInput
                name='name'
                placeholder='Nombre del tipo'
                control={control}
                autoFocus
                required
              />
              {existingType && typeName && (
                <div className='text-green-600 text-sm'>
                  ✓ El tipo "{existingType.name}" ya existe y será seleccionado
                </div>
              )}
              <div className='flex gap-3 justify-end'>
                <GlobalButton
                  variant={GlobalButtonVariant.secondaryButton}
                  action={handleClose}
                >
                  Cancelar
                </GlobalButton>
                <FormSubmitButton
                  label='Agregar tipo'
                  action={handleSubmit(onSubmit)}
                  isLoading={isCreatingType}
                />
              </div>
            </form>
          </div>
        </div>
      </Modal>
    </Authorize>
  );
};

export default AddTypeModal; 